import { useState, useEffect, useRef } from 'react';
import { useAuth, authHeaders } from '@/context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Package, MessageSquare, Users, BarChart3, ChevronRight, ChevronDown, Send, ArrowLeft, Search, Eye, Clock, Truck, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Mail as MailIcon, Paperclip, X, FileText, Image as ImageIcon, Play, Loader2, Square, Trash2, RotateCcw, DollarSign, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'order_received', label: 'Order Received', icon: Package, color: 'bg-blue-100 text-blue-700' },
  { value: 'under_review', label: 'Under Review', icon: Eye, color: 'bg-indigo-100 text-indigo-700' },
  { value: 'awaiting_customer_confirmation', label: 'Awaiting Confirmation', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'on_hold', label: 'On Hold', icon: AlertTriangle, color: 'bg-orange-100 text-orange-700' },
  { value: 'delayed', label: 'Delayed', icon: AlertTriangle, color: 'bg-orange-100 text-orange-700' },
  { value: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'bg-red-100 text-red-700' },
  { value: 'refunded', label: 'Refunded', icon: RefreshCw, color: 'bg-gray-100 text-gray-600' },
  { value: 'returned', label: 'Returned', icon: RefreshCw, color: 'bg-gray-100 text-gray-600' },
  { value: 'remake_in_progress', label: 'Remake In Progress', icon: RefreshCw, color: 'bg-amber-100 text-amber-700' },
  { value: 'in_production', label: 'In Production', icon: BarChart3, color: 'bg-amber-100 text-amber-700' },
  { value: 'shipped', label: 'Shipped', icon: Truck, color: 'bg-purple-100 text-purple-700' },
  { value: 'delivered', label: 'Delivered', icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
];

const statusColor = (s) => STATUS_OPTIONS.find(o => o.value === s)?.color || 'bg-gray-100 text-gray-600';
const statusLabel = (s) => STATUS_OPTIONS.find(o => o.value === s)?.label || s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';

const PRODUCT_TYPE_LABELS = { 'cover_and_foam': 'Cover & Foam', 'cover_only': 'Cover Only', 'foam_only': 'Foam Only' };
const ZIP_LABELS = { 'back': 'Back Edge', 'front': 'Front Edge', 'left': 'Left Edge', 'right': 'Right Edge', 'length': 'Along Length', 'width': 'Along Width', 'long-edge': 'Long Edge', 'short-edge': 'Short Edge' };
const PIPING_LABELS = { 'same': 'Same Material', 'different': 'Different Material' };

function fmtMm(val) {
  const mm = Math.round(val * 10);
  return mm.toLocaleString('en-AU') + ' mm';
}

function formatItemDimensions(item) {
  const d = item.dimensions;
  if (!d) return '-';
  const parts = [];
  if (d.length) parts.push(`${fmtMm(d.length)} L`);
  if (d.width) parts.push(`${fmtMm(d.width)} W`);
  if (d.thickness) parts.push(`${fmtMm(d.thickness)} T`);
  if (d.diameter) parts.push(`${fmtMm(d.diameter)} dia`);
  if (d.straight_depth) parts.push(`${fmtMm(d.straight_depth)} SD`);
  if (d.back_width) parts.push(`${fmtMm(d.back_width)} BW`);
  if (d.back_height) parts.push(`${fmtMm(d.back_height)} BH`);
  if (d.base_depth) parts.push(`${fmtMm(d.base_depth)} BD`);
  if (d.top_depth) parts.push(`${fmtMm(d.top_depth)} TD`);
  if (d.front_width) parts.push(`${fmtMm(d.front_width)} FW`);
  return parts.join('  x  ');
}

function OrderItemDetail({ item }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="py-3 border-b border-gray-50 last:border-0" data-testid={`admin-item-${item.id || item.cushion_name}`}>
      {item.preview_image && (
        <div className="mb-3 rounded-lg overflow-hidden w-full sm:max-w-[300px]">
          <img src={`${API}${item.preview_image}`} alt={item.cushion_name} className="w-full h-auto rounded-lg" />
        </div>
      )}
      <div className="flex justify-between items-start mb-1.5">
        <span className="font-semibold text-sm text-gray-900">{item.cushion_name || 'Item'}</span>
        <span className="font-semibold text-sm">${item.total_price?.toFixed(2)}</span>
      </div>
      <div className="text-xs text-gray-500 mt-1.5 flex flex-col gap-1">
        <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">{formatItemDimensions(item)}</span>
        {item.product_type && (
          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">{PRODUCT_TYPE_LABELS[item.product_type] || item.product_type}</span>
        )}
        {item.fabric_name && (
          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Fabric: {item.fabric_name}</span>
        )}
        {item.foam_name && (
          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Foam: {item.foam_name}</span>
        )}
        {item.zip_placement && (
          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Zip: {ZIP_LABELS[item.zip_placement] || item.zip_placement}</span>
        )}
        {item.piping_option && (
          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">
            Piping: {PIPING_LABELS[item.piping_option] || item.piping_option}
            {item.piping_fabric_name && item.piping_option === 'different' ? ` (${item.piping_fabric_name})` : ''}
          </span>
        )}
        {item.configuration?.facing && (
          <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Facing: {item.configuration.facing}</span>
        )}
        <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] self-start">Qty: {item.quantity}</span>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setViewState] = useState(() => location.pathname === '/admin/scraper' ? 'scraper' : 'orders');
  const setView = (v) => {
    setViewState(v);
    navigate(v === 'scraper' ? '/admin/scraper' : '/admin', { replace: true });
  };  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      navigate('/', { replace: true });
      toast.error('Admin access required');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => { if (user?.is_admin) fetchStats(); }, [user]);
  useEffect(() => { if (user?.is_admin) fetchOrders(); }, [user, statusFilter, page]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: authHeaders() });
      if (res.ok) setStats(await res.json());
    } catch { /* ignore */ }
  };

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await fetch(`${API}/admin/orders?${params}`, { headers: authHeaders() });
      if (res.ok) { const data = await res.json(); setOrders(data.orders); setTotalOrders(data.total); }
    } catch { /* ignore */ }
  };

  const openOrder = async (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setView('order-detail');
    try {
      const res = await fetch(`${API}/admin/orders/${order.order_id}/messages`, { headers: authHeaders() });
      if (res.ok) setMessages(await res.json());
      await fetch(`${API}/admin/orders/${order.order_id}/messages/read`, { method: 'PUT', headers: authHeaders() });
    } catch { /* ignore */ }
  };

  const updateStatus = async () => {
    if (!newStatus) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/orders/${selectedOrder.order_id}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status: newStatus, note: statusNote }),
      });
      if (res.ok) {
        toast.success('Status updated');
        setSelectedOrder({ ...selectedOrder, status: newStatus });
        setStatusNote('');
        fetchOrders();
      }
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const sendReply = async () => {
    if (!newMsg.trim() && !attachment) return;
    setSaving(true);
    let attachmentData = null;
    if (attachment) {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', attachment);
      try {
        const upRes = await fetch(`${API}/orders/${selectedOrder.order_id}/messages/upload`, { method: 'POST', headers: authHeaders(), body: formData });
        if (upRes.ok) { attachmentData = await upRes.json(); }
        else { toast.error('Failed to upload attachment'); setUploading(false); setSaving(false); return; }
      } catch { toast.error('Upload failed'); setUploading(false); setSaving(false); return; }
      setUploading(false);
    }
    try {
      const body = { content: newMsg.trim() || (attachmentData ? `Attachment: ${attachmentData.file_name}` : '') };
      if (attachmentData) body.attachment = attachmentData;
      const res = await fetch(`${API}/admin/orders/${selectedOrder.order_id}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages([...messages, msg]);
        setNewMsg(''); setAttachment(null);
        toast.success('Reply sent & customer notified');
      }
    } catch { toast.error('Failed to send'); }
    setSaving(false);
  };

  const handleAdminFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setAttachment(file);
  };

  const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

  const filteredOrders = searchQuery
    ? orders.filter(o => o.order_id.toLowerCase().includes(searchQuery.toLowerCase()) || o.customer?.email?.includes(searchQuery.toLowerCase()) || o.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : orders;

  if (authLoading || !user?.is_admin) return <div className="pt-20 sm:pt-[122px] min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-teal-500" /></div>;

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="admin-page">
      <div className="w-full px-4 sm:px-8 py-8">
        {(view === 'orders' || view === 'dashboard') && (
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl sm:text-3xl font-display font-semibold text-[#1A1A1A]">Admin Panel</h1>
            <Link to="/account" className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"><ArrowLeft className="w-3 h-3" />My Account</Link>
          </div>
        )}
        {(view === 'scraper' || view === 'order-detail') && (
          <div className="max-w-6xl mx-auto flex items-center justify-between mb-6">
            <h1 className="text-2xl sm:text-3xl font-display font-semibold text-[#1A1A1A]">Admin Panel</h1>
            <Link to="/account" className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"><ArrowLeft className="w-3 h-3" />My Account</Link>
          </div>
        )}

        {/* Dashboard */}
        {view === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard label="Total Orders" value={stats.total_orders} icon={Package} />
              <StatCard label="Customers" value={stats.total_users} icon={Users} />
              <StatCard label="Unread Messages" value={stats.unread_messages} icon={MessageSquare} accent={stats.unread_messages > 0} />
              <StatCard label="In Production" value={stats.orders_by_status?.in_production || 0} icon={BarChart3} />
            </div>
          </div>
        )}

        {/* Orders list */}
        {view === 'orders' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search by order ID, name, or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-10" data-testid="admin-search" />
              </div>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="h-10 rounded-lg border border-gray-200 px-3 text-sm bg-white" data-testid="admin-status-filter">
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="bg-white rounded-xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left p-3 font-medium text-gray-500">Order</th>
                    <th className="text-left p-3 font-medium text-gray-500">Name</th>
                    <th className="text-left p-3 font-medium text-gray-500 hidden md:table-cell">Email</th>
                    <th className="text-left p-3 font-medium text-gray-500 hidden md:table-cell">Location</th>
                    <th className="text-left p-3 font-medium text-gray-500 hidden sm:table-cell">Items</th>
                    <th className="text-left p-3 font-medium text-gray-500">Date</th>
                    <th className="text-left p-3 font-medium text-gray-500">Status</th>
                    <th className="text-right p-3 font-medium text-gray-500">Total</th>
                    <th className="text-right p-3 font-medium text-gray-500 w-10"></th>
                  </tr></thead>
                  <tbody>
                    {filteredOrders.map(o => {
                      const addr = o.customer?.address;
                      const suburb = addr ? `${addr.city || ''}${addr.state ? ' ' + addr.state : ''}${addr.postcode ? ' ' + addr.postcode : ''}`.trim() : '';
                      const orderDate = new Date(o.created_at);
                      const dateStr = orderDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Sydney' });
                      const timeStr = orderDate.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Australia/Sydney' }).toLowerCase();
                      return (
                      <tr key={o.order_id} onClick={() => openOrder(o)} className="border-b border-gray-50 hover:bg-teal-50/30 cursor-pointer transition-colors" data-testid={`admin-order-${o.order_id}`}>
                        <td className="p-3 font-mono text-xs font-medium">
                          <div className="flex items-center gap-2">
                            {o.order_id}
                            {o.unread_count > 0 && (
                              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse" data-testid={`unread-badge-${o.order_id}`}>
                                <MailIcon className="w-2.5 h-2.5" />
                                {o.unread_count}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-sm font-medium">{o.customer?.name}</td>
                        <td className="p-3 hidden md:table-cell text-xs text-gray-400">{o.customer?.email}</td>
                        <td className="p-3 hidden md:table-cell text-xs text-gray-500">{suburb || '-'}</td>
                        <td className="p-3 hidden sm:table-cell text-gray-500 text-xs">{o.items?.length || 0} item{(o.items?.length || 0) !== 1 ? 's' : ''}</td>
                        <td className="p-3 text-xs text-gray-500"><div>{dateStr}</div><div className="text-gray-400">{timeStr}</div></td>
                        <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(o.status)}`}>{statusLabel(o.status)}</span></td>
                        <td className="p-3 text-right font-semibold">${o.total?.toFixed(2)}</td>
                        <td className="p-3 text-right"><Eye className="w-4 h-4 text-gray-300" /></td>
                      </tr>
                      );
                    })}
                    {filteredOrders.length === 0 && <tr><td colSpan={9} className="p-12 text-center text-gray-400">No orders found</td></tr>}
                  </tbody>
                </table>
              </div>
              {totalOrders > 20 && (
                <div className="flex items-center justify-between p-3 border-t border-gray-100">
                  <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                  <span className="text-xs text-gray-400">Page {page} of {Math.ceil(totalOrders / 20)}</span>
                  <Button variant="ghost" size="sm" disabled={page >= Math.ceil(totalOrders / 20)} onClick={() => setPage(page + 1)}>Next</Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Order detail */}
        {view === 'order-detail' && selectedOrder && (
          <div className="max-w-6xl mx-auto">

            <div className="grid lg:grid-cols-5 gap-5">
              {/* Order info - left column */}
              <div className="lg:col-span-3 space-y-5">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-card p-5 sm:p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-display font-semibold font-mono">{selectedOrder.order_id}</h2>
                      <p className="text-xs text-gray-400 mt-0.5">{new Date(selectedOrder.created_at).toLocaleString('en-AU')}</p>
                    </div>
                    <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${statusColor(selectedOrder.status)}`}>{statusLabel(selectedOrder.status)}</span>
                  </div>

                  {/* Customer info */}
                  <div className="grid sm:grid-cols-2 gap-4 p-4 bg-cream rounded-xl text-sm">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Customer</p>
                      <p className="font-semibold">{selectedOrder.customer?.name}</p>
                      <p className="text-gray-500">{selectedOrder.customer?.email}</p>
                      {selectedOrder.customer?.phone && <p className="text-gray-500">{selectedOrder.customer?.phone}</p>}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Shipping Address</p>
                      {selectedOrder.customer?.address?.line1 ? (
                        <p className="text-gray-600">
                          {selectedOrder.customer.address.line1}<br />
                          {[selectedOrder.customer.address.city, selectedOrder.customer.address.state, selectedOrder.customer.address.postcode].filter(Boolean).join(' ')}
                        </p>
                      ) : (
                        <p className="text-gray-400 italic">Not provided</p>
                      )}
                    </div>
                  </div>

                  {selectedOrder.delivery_option === 'pickup' && (
                    <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-200 text-sm">
                      <span className="font-medium text-teal-700">Factory Pickup</span>
                    </div>
                  )}

                  {selectedOrder.notes && (
                    <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-sm">
                      <span className="font-medium text-yellow-800">Notes: </span>
                      <span className="text-yellow-700">{selectedOrder.notes}</span>
                    </div>
                  )}
                </div>

                {/* Items */}
                <div className="bg-white rounded-xl shadow-card p-5 sm:p-6">
                  <h3 className="text-base font-display font-semibold mb-3">Order Items ({selectedOrder.items?.length || 0})</h3>
                  <div className="divide-y divide-gray-50">
                    {selectedOrder.items?.map((item, i) => (
                      <OrderItemDetail key={i} item={item} />
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                    <span className="font-semibold text-gray-600">Order Total</span>
                    <span className="text-xl font-bold text-teal-600">${selectedOrder.total?.toFixed(2)}</span>
                  </div>
                </div>

                {/* Status History */}
                {selectedOrder.status_history?.length > 0 && (
                  <div className="bg-white rounded-xl shadow-card p-5 sm:p-6">
                    <h3 className="text-base font-display font-semibold mb-3">Status History</h3>
                    <div className="space-y-2">
                      {selectedOrder.status_history.map((entry, i) => (
                        <div key={i} className="flex items-start gap-3 text-sm py-2 border-b border-gray-50 last:border-0">
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${statusColor(entry.status).includes('green') ? 'bg-green-500' : statusColor(entry.status).includes('red') ? 'bg-red-500' : statusColor(entry.status).includes('blue') ? 'bg-blue-500' : 'bg-gray-400'}`} />
                          <div className="flex-1">
                            <span className="font-medium">{statusLabel(entry.status)}</span>
                            {entry.note && <span className="text-gray-500"> - {entry.note}</span>}
                            <div className="text-xs text-gray-400 mt-0.5">
                              {new Date(entry.timestamp).toLocaleString('en-AU')} {entry.updated_by && `by ${entry.updated_by}`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right column - actions & messages */}
              <div className="lg:col-span-2 space-y-5">
                {/* Status update */}
                <div className="bg-white rounded-xl shadow-card p-5">
                  <h3 className="text-base font-display font-semibold mb-3">Update Status</h3>
                  <div className="space-y-2 mb-3">
                    {STATUS_OPTIONS.map(s => (
                      <label
                        key={s.value}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all text-sm ${
                          newStatus === s.value
                            ? 'bg-teal-50 border-2 border-teal-500'
                            : 'border-2 border-transparent hover:bg-gray-50'
                        } ${s.value === selectedOrder.status ? 'opacity-50' : ''}`}
                        data-testid={`status-option-${s.value}`}
                      >
                        <input
                          type="radio"
                          name="order-status"
                          value={s.value}
                          checked={newStatus === s.value}
                          onChange={() => setNewStatus(s.value)}
                          className="sr-only"
                        />
                        <span className={`w-3 h-3 rounded-full flex-shrink-0 ${newStatus === s.value ? 'bg-teal-500' : 'bg-gray-200'}`} />
                        <span className={`flex-1 ${newStatus === s.value ? 'font-semibold text-teal-700' : 'text-gray-700'}`}>{s.label}</span>
                        {s.value === selectedOrder.status && <span className="text-[10px] text-gray-400 font-medium">Current</span>}
                      </label>
                    ))}
                  </div>
                  <Input placeholder="Add a note (optional)" value={statusNote} onChange={e => setStatusNote(e.target.value)} className="mb-3 h-10" data-testid="admin-status-note" />
                  <Button onClick={updateStatus} disabled={saving || newStatus === selectedOrder.status} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="admin-update-status-btn">
                    {saving ? 'Updating...' : 'Update Status'}
                  </Button>
                </div>

                {/* Messages */}
                <div className="bg-white rounded-xl shadow-card p-5">
                  <h3 className="text-base font-display font-semibold mb-3 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Messages
                  </h3>
                  <div className="max-h-80 overflow-y-auto space-y-4 mb-4">
                    {messages.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No messages for this order</p>}
                    {messages.map(m => (
                      <div key={m.message_id} className={`flex pb-3 ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 ${m.sender === 'admin' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                          <p className={`text-[10px] font-medium mb-0.5 ${m.sender === 'admin' ? 'opacity-70' : 'text-gray-400'}`}>
                            {m.sender_name} &middot; {new Date(m.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {m.attachment && m.attachment.file_url && (
                            <div className="mb-1.5">
                              {isImageUrl(m.attachment.file_url) ? (
                                <a href={m.attachment.file_url} target="_blank" rel="noopener noreferrer">
                                  <img src={m.attachment.file_url} alt={m.attachment.file_name} className="max-w-full max-h-48 rounded-lg border border-white/20" />
                                </a>
                              ) : (
                                <a href={m.attachment.file_url} target="_blank" rel="noopener noreferrer"
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${m.sender === 'admin' ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'}`}>
                                  <FileText className="w-3.5 h-3.5" />{m.attachment.file_name}
                                </a>
                              )}
                            </div>
                          )}
                          <p className="text-sm">{m.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {attachment && (
                    <div className="mb-3 flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                      {attachment.type?.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-gray-500" /> : <FileText className="w-4 h-4 text-gray-500" />}
                      <span className="text-xs text-gray-600 truncate flex-1">{attachment.name}</span>
                      <button onClick={() => setAttachment(null)} className="text-gray-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" onChange={handleAdminFileSelect} />
                    <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()} className="h-10 w-10 flex-shrink-0 text-gray-400 hover:text-teal-600" data-testid="admin-attach-file-btn">
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <Input placeholder="Reply to customer..." value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendReply()} className="flex-1 h-10" data-testid="admin-reply-input" />
                    <Button onClick={sendReply} disabled={saving || uploading || (!newMsg.trim() && !attachment)} size="icon" className="h-10 w-10 bg-teal-600 hover:bg-teal-700 rounded-full flex-shrink-0" data-testid="admin-send-reply-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scraper Dashboard */}
        {view === 'scraper' && <div className="max-w-6xl mx-auto"><ScraperDashboard /></div>}
      </div>
    </div>
  );
}

function RangeResultRow({ rr, index }) {
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggleExpand = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (details || rr.status === 'failed' || rr.status === 'discontinued') return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/scraper/catalogue/${rr.slug}/colours`, { headers: authHeaders() });
      if (res.ok) { setDetails(await res.json()); }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const stockBadge = (c) => {
    if (c.stock_meters === -1 || c.stock_meters === undefined) return <span className="text-gray-400 text-[10px]">N/A</span>;
    if (c.stock_meters === 0) return <span className="text-red-500 text-[10px] font-medium">Out</span>;
    const prev = c.previous_stock_meters;
    // prev may be null/undefined on first scrape or -1 (N/A previously)
    let colorClass = 'text-gray-700';
    let arrow = '';
    let title = '';
    if (typeof prev === 'number' && prev >= 0) {
      if (c.stock_meters > prev) { colorClass = 'text-green-600'; arrow = ' ↑'; title = `Was ${prev}m`; }
      else if (c.stock_meters < prev) { colorClass = 'text-orange-500'; arrow = ' ↓'; title = `Was ${prev}m`; }
    }
    return <span className={`${colorClass} text-[10px] font-medium`} title={title}>{c.stock_meters}m{arrow}</span>;
  };

  const statusIcon = rr.status === 'success'
    ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
    : rr.status === 'discontinued'
    ? <Trash2 className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
    : <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />;

  const rowBg = rr.status === 'failed' ? 'bg-red-50/30' : rr.status === 'discontinued' ? 'bg-orange-50/30' : '';

  return (
    <div className={`border-b border-gray-100 last:border-0 ${rowBg}`} data-testid={`range-row-${rr.slug}`}>
      <div className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50/80 transition-colors" onClick={toggleExpand}>
        {rr.status === 'success'
          ? <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${expanded ? '' : '-rotate-90'}`} />
          : statusIcon
        }
        <span className="text-[10px] text-gray-400 w-5 text-right">{index + 1}</span>
        <span className="text-xs font-medium flex-1 truncate">{rr.name}</span>
        {rr.warning && <span className="text-[9px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full">stock N/A</span>}
        <span className="text-[10px] text-gray-500 w-14 text-center">{rr.colors || 0} col</span>
        {rr.status === 'failed' && <span className="text-[9px] text-red-500 truncate max-w-[100px]">{rr.error?.substring(0, 30)}</span>}
        {rr.status === 'discontinued' && <span className="text-[9px] text-orange-500">Discontinued</span>}
      </div>

      {expanded && rr.status === 'success' && (
        <div className="bg-gray-50/60 px-4 pb-3">
          {loading && <div className="flex items-center gap-2 py-3 text-xs text-gray-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading details...</div>}
          {details && (
            <div className="space-y-3 mt-1">
              {/* Banner image + info layout */}
              <div className="flex gap-4">
                {details.collection_image_url && (
                  <div className="w-[20%] flex-shrink-0">
                    <img
                      src={details.collection_image_url}
                      alt={details.range_name}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="w-full rounded-lg object-cover aspect-[4/3] border border-gray-100"
                    />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  {/* Range info cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {details.retail_price > 0 && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-[9px] uppercase text-gray-400 font-medium">Retail Price</p>
                    <p className="text-sm font-bold text-green-700">${details.retail_price?.toFixed(2)}/m</p>
                  </div>
                )}
                {details.wholesale_price > 0 && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-[9px] uppercase text-gray-400 font-medium">Wholesale</p>
                    <p className="text-sm font-bold text-gray-700">${details.wholesale_price?.toFixed(2)}/m</p>
                  </div>
                )}
                {details.composition && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-[9px] uppercase text-gray-400 font-medium">Composition</p>
                    <p className="text-xs font-medium text-gray-700 leading-tight">{details.composition}</p>
                  </div>
                )}
                {details.martindale && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-[9px] uppercase text-gray-400 font-medium">Martindale</p>
                    <p className="text-xs font-medium text-gray-700">{details.martindale}</p>
                  </div>
                )}
                {details.pattern_repeat && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-[9px] uppercase text-gray-400 font-medium">Pattern Repeat</p>
                    <p className="text-xs font-medium text-gray-700">{details.pattern_repeat}</p>
                  </div>
                )}
                {details.pattern_match && (
                  <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <p className="text-[9px] uppercase text-gray-400 font-medium">Pattern Match</p>
                    <p className="text-xs font-medium text-gray-700">{details.pattern_match}</p>
                  </div>
                )}
              </div>
                </div>
              </div>

              {/* Fabric types */}
              {details.fabric_types && details.fabric_types.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] uppercase text-gray-400 font-medium mr-1">Filters:</span>
                  {details.fabric_types.map((ft, i) => (
                    <span key={i} className="text-[10px] bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full font-medium">{ft}</span>
                  ))}
                </div>
              )}

              {/* Collection description */}
              {details.collection_description && (
                <p className="text-xs text-gray-500 italic leading-relaxed">{details.collection_description}</p>
              )}

              {/* Colour table — rendered as 2 logical columns (6 cells per row) */}
              {details.colours && details.colours.length > 0 && (
                <div>
                  <h5 className="text-[10px] uppercase text-gray-400 font-medium mb-1.5">Colours ({details.colours.length})</h5>
                  <div className="max-h-[300px] overflow-y-auto rounded-lg border border-gray-100 bg-white">
                    <table className="w-full text-xs table-fixed">
                      <thead className="sticky top-0 bg-gray-50 z-10">
                        <tr className="text-[9px] uppercase text-gray-400">
                          <th className="px-2 py-1.5 text-left font-medium w-8"></th>
                          <th className="px-2 py-1.5 text-left font-medium">Colour</th>
                          <th className="px-2 py-1.5 text-left font-medium">Category</th>
                          <th className="px-2 py-1.5 text-right font-medium w-14">Stock</th>
                          <th className="px-2 py-1.5 text-left font-medium w-8 border-l border-gray-200"></th>
                          <th className="px-2 py-1.5 text-left font-medium">Colour</th>
                          <th className="px-2 py-1.5 text-left font-medium">Category</th>
                          <th className="px-2 py-1.5 text-right font-medium w-14">Stock</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {Array.from({ length: Math.ceil(details.colours.length / 2) }).map((_, ri) => {
                          const left = details.colours[ri * 2];
                          const right = details.colours[ri * 2 + 1];
                          const renderCell = (c, isRight = false) => (
                            <>
                              <td className={`px-2 py-1 ${isRight ? 'border-l border-gray-100' : ''}`}>
                                {c
                                  ? (c.image_url
                                    ? <img src={c.image_url} alt="" className="w-6 h-6 rounded object-cover border border-gray-200" />
                                    : c.hex_color
                                      ? <div className="w-6 h-6 rounded border border-gray-200" style={{ backgroundColor: c.hex_color }} />
                                      : <div className="w-6 h-6 rounded bg-gray-100 border border-gray-200" />)
                                  : null}
                              </td>
                              <td className="px-2 py-1 font-medium text-gray-700 truncate">{c?.name || ''}</td>
                              <td className="px-2 py-1">
                                {c?.color_category && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{c.color_category}</span>}
                              </td>
                              <td className="px-2 py-1 text-right">{c ? stockBadge(c) : null}</td>
                            </>
                          );
                          return (
                            <tr key={ri} className="hover:bg-gray-50/60">
                              {renderCell(left, false)}
                              {renderCell(right, true)}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {details.colours && details.colours.length === 0 && <p className="text-xs text-gray-400 py-1">No colour data found.</p>}
            </div>
          )}
        </div>
      )}
      {expanded && rr.status === 'failed' && (
        <div className="bg-red-50/40 px-4 pb-3 pt-1">
          <p className="text-xs text-red-500">{rr.error || 'Unknown error'}</p>
        </div>
      )}
      {expanded && rr.status === 'discontinued' && (
        <div className="bg-orange-50/40 px-4 pb-3 pt-1">
          <p className="text-xs text-orange-600">This range has been removed from Warwick's website and deleted from the database.</p>
        </div>
      )}
    </div>
  );
}

function ScraperDashboard() {
  const [runs, setRuns] = useState([]);
  const [starting, setStarting] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [liveProgress, setLiveProgress] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const [tickNow, setTickNow] = useState(() => Date.now());
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const fetchRuns = async () => {
    try {
      const res = await fetch(`${API}/admin/scraper/runs`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRuns(data);
        const running = data.find(r => r.status === 'in_progress');
        if (running) { setActiveRunId(running.run_id); setLiveProgress(running); }
        else { setActiveRunId(null); setLiveProgress(null); }
      }
    } catch {}
  };

  useEffect(() => { fetchRuns(); }, []);

  useEffect(() => {
    if (!activeRunId) { if (tickRef.current) clearInterval(tickRef.current); return; }
    setTickNow(Date.now());
    tickRef.current = setInterval(() => setTickNow(Date.now()), 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [activeRunId]);

  useEffect(() => {
    if (!activeRunId) { if (pollRef.current) clearInterval(pollRef.current); return; }
    const poll = async () => {
      try {
        const res = await fetch(`${API}/admin/scraper/progress/${activeRunId}`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setLiveProgress(data);
          if (data.status !== 'in_progress') { setActiveRunId(null); fetchRuns(); }
        }
      } catch {}
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRunId]);

  const startScrape = async () => {
    setStarting(true);
    try {
      const res = await fetch(`${API}/admin/scraper/start`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setActiveRunId(data.run_id);
        toast.success('Catalogue scrape started');
        fetchRuns();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to start'); }
    } catch { toast.error('Failed to start scraper'); }
    setStarting(false);
  };

  const stopRun = async (runId) => {
    try {
      const res = await fetch(`${API}/admin/scraper/stop/${runId}`, { method: 'POST', headers: authHeaders() });
      if (res.ok) { toast.success('Scrape stopping...'); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to stop'); }
    } catch { toast.error('Failed to stop'); }
  };

  const deleteRun = async (runId) => {
    if (!window.confirm('Delete this run from history?')) return;
    try {
      const res = await fetch(`${API}/admin/scraper/runs/${runId}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { toast.success('Run deleted'); fetchRuns(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete'); }
  };

  const rescrapeFailed = async (runId) => {
    try {
      const res = await fetch(`${API}/admin/scraper/rescrape-failed/${runId}`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setActiveRunId(data.run_id);
        toast.success(`Re-scraping ${data.ranges_to_rescrape} failed ranges`);
        fetchRuns();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to start rescrape'); }
  };

  const resumeRun = async (runId) => {
    try {
      const res = await fetch(`${API}/admin/scraper/resume/${runId}`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setActiveRunId(data.run_id);
        toast.success(`Resuming scrape — skipping ${data.skipped} completed ranges`);
        fetchRuns();
      } else { const err = await res.json(); toast.error(err.detail || 'Failed to resume'); }
    } catch { toast.error('Failed to resume scrape'); }
  };

  const fmtDuration = (s) => { if (!s) return '-'; const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60; if (h > 0) return `${h}h ${m}m ${sec}s`; return m > 0 ? `${m}m ${sec}s` : `${sec}s`; };
  const fmtHMS = (s) => {
    if (s == null || !isFinite(s) || s < 0) return '--:--:--';
    const sec = Math.floor(s);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const ss = sec % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };
  const fmtDate = (iso) => { if (!iso) return '-'; return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };

  let liveElapsedSec = null;
  if (liveProgress && liveProgress.status === 'in_progress' && liveProgress.started_at) {
    try {
      const startedMs = new Date(liveProgress.started_at).getTime();
      liveElapsedSec = Math.max(0, (tickNow - startedMs) / 1000);
    } catch { /* ignore */ }
  }

  const triggerLabel = (t) => ({ scheduled: 'Scheduled', manual: 'Manual', rescrape: 'Rescrape', resume: 'Resumed', filter_scrape: 'Filter (Legacy)', collection_scrape: 'Collection (Legacy)' })[t] || t;
  const triggerColor = (t) => ({ scheduled: 'bg-blue-50 text-blue-700', manual: 'bg-amber-50 text-amber-700', rescrape: 'bg-purple-50 text-purple-700', resume: 'bg-green-50 text-green-700', filter_scrape: 'bg-violet-50 text-violet-700', collection_scrape: 'bg-cyan-50 text-cyan-700' })[t] || 'bg-gray-50 text-gray-700';

  return (
    <div className="space-y-6" data-testid="scraper-dashboard">
      {liveProgress && liveProgress.status === 'in_progress' && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 mt-6" data-testid="scraper-live-progress">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />
              <h3 className="font-semibold text-teal-800">Scrape In Progress</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => stopRun(activeRunId)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid="stop-scrape-btn">
              <Square className="w-4 h-4 mr-1" /> Stop
            </Button>
          </div>
          <div className="w-full bg-teal-200/50 rounded-full h-3 mb-2">
            <div className="bg-teal-600 h-3 rounded-full transition-all duration-500" style={{ width: `${liveProgress.progress_pct || 0}%` }} data-testid="scraper-progress-bar" />
          </div>
          <div className="flex justify-between text-sm text-teal-700">
            <span>{Math.round(liveProgress.progress_pct || 0)}%</span>
            <span>{liveProgress.ranges_scraped || 0} / {liveProgress.total_ranges || '?'} ranges</span>
          </div>
          {liveProgress.current_range && (
            <p className="text-xs text-teal-600 mt-1">Currently scraping: <span className="font-medium">{liveProgress.current_range}</span></p>
          )}
          <div className="mt-3 pt-3 border-t border-teal-200/60" data-testid="scraper-elapsed-timer">
            <p className="text-[10px] uppercase tracking-wide text-teal-600/70 font-medium mb-0.5">Elapsed</p>
            <p className="text-sm tabular-nums">{fmtHMS(liveElapsedSec)}</p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 flex-wrap">
        <Button onClick={startScrape} disabled={starting || !!activeRunId} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6" data-testid="start-scrape-btn">
          {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          {activeRunId ? 'Scrape Running...' : 'Start New Scrape'}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { fetchRuns(); }} data-testid="refresh-runs-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="p-5 border-b">
          <h3 className="font-display font-semibold text-base">Scrape History</h3>
        </div>
        {runs.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No scraper runs yet. Click "Start New Scrape" to begin.</div>
        ) : (
          <div className="divide-y">
            {runs.map((run) => {
              const isExpanded = expandedRun === run.run_id;
              const statusColor = run.status === 'completed' ? 'bg-green-50 text-green-700' : run.status === 'in_progress' ? 'bg-teal-50 text-teal-700' : run.status === 'cancelled' ? 'bg-amber-50 text-amber-700' : run.status === 'interrupted' ? 'bg-orange-50 text-orange-700' : run.status === 'partial' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700';
              const statusLabel = run.status === 'completed' ? 'Success' : run.status === 'in_progress' ? 'Running' : run.status === 'cancelled' ? 'Cancelled' : run.status === 'interrupted' ? 'Interrupted' : run.status === 'partial' ? 'Partial' : 'Failed';
              const StatusIcon = run.status === 'completed' ? CheckCircle2 : run.status === 'in_progress' ? Loader2 : run.status === 'cancelled' ? Square : run.status === 'interrupted' ? AlertTriangle : run.status === 'partial' ? AlertTriangle : XCircle;
              return (
                <div key={run.run_id} data-testid={`scraper-run-${run.run_id}`}>
                  <div className="flex items-start sm:items-center gap-2 sm:gap-3 px-4 sm:px-5 py-3 sm:py-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpandedRun(isExpanded ? null : run.run_id)}>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 mt-1 sm:mt-0 ${isExpanded ? '' : '-rotate-90'}`} />
                    {/* Mobile layout */}
                    <div className="flex-1 min-w-0 sm:hidden">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${triggerColor(run.trigger)}`}>{triggerLabel(run.trigger)}</span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}><StatusIcon className={`w-3 h-3 ${run.status === 'in_progress' ? 'animate-spin' : ''}`} />{statusLabel}</span>
                      </div>
                      <p className="text-sm text-gray-700">{fmtDate(run.started_at)}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span className="font-medium text-gray-700">
                          {run.ranges_succeeded !== undefined
                            ? <>{run.ranges_succeeded}<span className="text-gray-400">/{run.total_ranges}</span>{run.ranges_failed > 0 && <span className="text-red-500 ml-1">({run.ranges_failed} failed)</span>}</>
                            : `${run.ranges_scraped || 0}/${run.total_ranges || '?'}`}
                        </span>
                        {run.trigger !== 'filter_scrape' && run.total_colours > 0 && <span>{run.total_colours?.toLocaleString()} colours</span>}
                        {run.trigger !== 'filter_scrape' && run.total_stock_changes > 0 && <span className="text-teal-600 font-medium" data-testid={`run-changes-${run.run_id}`}>{run.total_stock_changes.toLocaleString()} changes</span>}
                        {run.duration_seconds > 0 && <span>{fmtDuration(run.duration_seconds)}</span>}
                      </div>
                    </div>
                    {/* Desktop layout */}
                    <div className="flex-1 min-w-0 hidden sm:grid sm:grid-cols-6 gap-2 items-center text-sm">
                      <div className="whitespace-nowrap">
                        <span className="text-gray-700">{fmtDate(run.started_at)}</span>
                        {run.completed_at && run.status !== 'in_progress' && (
                          <span className="block text-xs text-gray-400 mt-0.5">Ended: {fmtDate(run.completed_at)}</span>
                        )}
                      </div>
                      <span><span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${triggerColor(run.trigger)}`}>{triggerLabel(run.trigger)}</span></span>
                      <span><span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusColor}`}><StatusIcon className={`w-3 h-3 ${run.status === 'in_progress' ? 'animate-spin' : ''}`} />{statusLabel}</span></span>
                      <span className="text-center font-medium">
                        {run.ranges_succeeded !== undefined
                          ? <>{run.ranges_succeeded}<span className="text-gray-400">/{run.total_ranges}</span>{run.ranges_failed > 0 && <span className="text-red-500 text-xs ml-1">({run.ranges_failed} failed)</span>}</>
                          : `${run.ranges_scraped || 0}/${run.total_ranges || '?'}`}
                      </span>
                      <span className="text-center">{run.trigger === 'filter_scrape' ? '' : <>{run.total_colours?.toLocaleString() || '-'} colours{run.total_stock_changes > 0 && <span className="text-teal-600 ml-1 font-medium">({run.total_stock_changes.toLocaleString()} changes)</span>}</>}</span>
                      <span className="text-center text-gray-500" data-testid={`run-duration-${run.run_id}`}>{fmtDuration(run.duration_seconds)}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {(run.status === 'cancelled' || run.status === 'failed' || run.status === 'interrupted' || run.status === 'partial') && run.ranges_succeeded > 0 && (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); resumeRun(run.run_id); }} title="Resume scrape" className="text-green-600 hover:text-green-800 hover:bg-green-50 h-8 w-8 p-0" data-testid={`resume-btn-${run.run_id}`} disabled={!!activeRunId}>
                          <Play className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {run.status !== 'in_progress' && run.ranges_failed > 0 && (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); rescrapeFailed(run.run_id); }} title="Re-scrape failed ranges" className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 h-8 w-8 p-0" data-testid={`rescrape-btn-${run.run_id}`} disabled={!!activeRunId}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {run.status !== 'in_progress' && (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteRun(run.run_id); }} title="Delete run" className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0" data-testid={`delete-btn-${run.run_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="bg-gray-50/80 border-t px-5 py-4">
                      {/* Failed ranges section */}
                      {run.failed_ranges && run.failed_ranges.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-red-600 uppercase mb-2">Failed Ranges ({run.failed_ranges.length})</h4>
                          <div className="space-y-1.5">
                            {run.failed_ranges.map((f, i) => (
                              <div key={i} className="flex items-start gap-2 text-xs bg-red-50 rounded-lg px-3 py-2">
                                <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                                <div>
                                  <span className="font-medium text-red-700">{f.name || f.slug}</span>
                                  <p className="text-red-500 mt-0.5">{f.error}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Range results section */}
                      {run.range_results && run.range_results.length > 0 ? (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">All Ranges ({run.range_results.length})</h4>
                          <div className="max-h-[500px] overflow-y-auto rounded-lg border bg-white">
                            {[...run.range_results].sort((a, b) => (b.status === 'failed' ? 1 : 0) - (a.status === 'failed' ? 1 : 0)).map((rr, i) => (
                              <RangeResultRow key={i} rr={rr} index={i} />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">No detailed range data available for this run.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl p-4 sm:p-5 ${accent ? 'bg-teal-50 border border-teal-200' : 'bg-white shadow-card'}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className={`w-5 h-5 mb-2 ${accent ? 'text-teal-600' : 'text-gray-400'}`} />
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}